import React, {useEffect, useState} from "react";
import _ from "lodash";
import {
    CompanyFilter,
    DateFilter,
    PoolCourierFilter,
    PoolCourierShiftSelectionCountFilter,
    PoolFilter,
    SubCompanyFilter
} from "../../../_defaultComponents/FilterComponents";

import {useHistory} from "react-router-dom";
import moment from "moment";
import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {FilterLabel} from "../../../_defaultComponents/FilterComponents/FilterComponentStyles";
import {isRealPool} from "../../../../utils/poolUtils";

const Filters = ({filters, setFilters}) => {
    const history = useHistory();
    const stores = useStore();
    const isAgency = stores.meStore.checkRole("isAgency");
    const [poolId, setPoolId] = useState(filters.pool);
    const [date, setDate] = useState(filters.date);
    const [courierId, setCourierId] = useState(filters.courierId);
    const [courierShiftSelectionCount, setCourierShiftSelectionCount] = useState(
        filters.courierShiftSelectionCount
    );
    const [companyId, setCompanyId] = useState(filters.companyId);
    const [subCompanyId, setSubCompanyId] = useState(filters.subCompanyId);

    // Acente için: gerçek havuzları (birden fazla alt firmalı) dedike entegrasyon havuzlarından
    // ayırt edebilmek ve Firma/Alt Firma seçimini bir havuza çözümleyebilmek için tüm havuz
    // listesi çekilir.
    const [, allPools] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.poolStore.getPoolList.bind(null, {limit: 1000, page: 1, city: "all"}),
        initialCall: isAgency,
        dependencies: [isAgency],
        condition: isAgency
    });

    const filterCompanyByDedicatedIntegrationPools = company =>
        (allPools?.data || []).some(
            pool =>
                pool.integrationType === "dedicatedIntegration" &&
                pool.companies?.some(poolCompany => poolCompany._id === company._id)
        );

    const handleUrlSearchParam = () => {
        const urlSearchParams = new URLSearchParams(history.location.search);
        const urlPoolId = urlSearchParams.get("poolId");
        if (urlPoolId) {
            setPoolId(urlPoolId);
        }

        const urlDate = urlSearchParams.get("date");
        if (urlDate && moment(urlDate).isValid()) {
            setDate(moment(urlDate));
        }

        const urlCourierId = urlSearchParams.get("courierId");
        if (urlCourierId) {
            setCourierId(urlCourierId);
        }

        const urlCourierShiftSelectionCount = urlSearchParams.get("courierShiftSelectionCount");
        if (urlCourierShiftSelectionCount) {
            setCourierShiftSelectionCount(urlCourierShiftSelectionCount);
        }

        const urlCompanyId = urlSearchParams.get("companyId");
        if (urlCompanyId) {
            setCompanyId(urlCompanyId);
        }

        const urlSubCompanyId = urlSearchParams.get("subCompanyId");
        if (urlSubCompanyId) {
            setSubCompanyId(urlSubCompanyId);
        }
    };

    const setUrl = () => {
        let path = history?.location?.pathname;
        path += "?";

        if (poolId) {
            path += "poolId=" + poolId;
        }

        if (date) {
            path += "&date=" + date.format("YYYY-MM-DD");
        }

        if (courierId) {
            path += "&courierId=" + courierId;
        }

        if (courierShiftSelectionCount) {
            path += "&courierShiftSelectionCount=" + courierShiftSelectionCount;
        }

        if (companyId) {
            path += "&companyId=" + companyId;
        }

        if (subCompanyId) {
            path += "&subCompanyId=" + subCompanyId;
        }

        history.push(path);
    };

    // Acente için: Firma/Alt Firma seçimi, eşleşen dedike entegrasyon havuzunu çözüp havuz
    // filtresine yansıtır.
    useEffect(() => {
        if (!isAgency || !companyId) return;
        const matchedPool = (allPools?.data || []).find(
            pool =>
                pool.integrationType === "dedicatedIntegration" &&
                pool.companies?.some(company => company._id === companyId) &&
                (!subCompanyId ||
                    pool.subCompanies?.some(subCompany => subCompany._id === subCompanyId))
        );
        setPoolId(matchedPool?._id);
    }, [isAgency, companyId, subCompanyId, allPools]);

    const handlePoolChange = value => {
        setPoolId(value);
        if (isAgency) {
            setCompanyId(undefined);
            setSubCompanyId(undefined);
        }
    };

    const handleCompanyChange = value => {
        setCompanyId(value);
        setSubCompanyId(undefined);
    };

    useEffect(() => {
        const newFilters = {
            poolId,
            date,
            courierId,
            courierShiftSelectionCount,
            companyId,
            subCompanyId
        };
        if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
            setFilters(newFilters);
            setUrl();
        }
    }, [poolId, date, courierId, courierShiftSelectionCount, companyId, subCompanyId]);

    useEffect(() => {
        handleUrlSearchParam();
    }, []);

    return (
        <React.Fragment>
            <div className="filter-item">
                <FilterLabel>Tarih</FilterLabel>
                <DateFilter
                    value={date}
                    onChange={setDate}
                    showAllOption={false}
                    showLabel={false}
                />
            </div>
            <div className="filter-item">
                <FilterLabel>Havuz Bölgesi</FilterLabel>
                <PoolFilter
                    value={poolId}
                    onChange={handlePoolChange}
                    showAllOption={false}
                    optionFilter={isAgency ? isRealPool : undefined}
                    showLabel={false}
                    marginBottom="0px"
                />
            </div>
            {isAgency && (
                <React.Fragment>
                    <div className="filter-item">
                        <FilterLabel>Firma</FilterLabel>
                        <CompanyFilter
                            showAllOption={false}
                            showPoolOption={false}
                            allowClear
                            value={companyId}
                            onChange={handleCompanyChange}
                            optionFilter={filterCompanyByDedicatedIntegrationPools}
                            hideTitle
                            selectMarginBottom="0px"
                        />
                    </div>
                    <div className="filter-item">
                        <FilterLabel>Alt Firma</FilterLabel>
                        <SubCompanyFilter
                            company={companyId}
                            value={subCompanyId}
                            onChange={setSubCompanyId}
                            disabled={!companyId}
                            initialCall={false}
                            showAllOption={false}
                            allowClear
                            hideTitle
                            selectMarginBottom="0px"
                        />
                    </div>
                </React.Fragment>
            )}
            <div className="filter-item">
                <FilterLabel>Kurye</FilterLabel>
                <PoolCourierFilter
                    value={courierId}
                    onChange={setCourierId}
                    poolId={poolId}
                    allowClear={true}
                    showLabel={false}
                    selectMarginBottom="0px"
                />
            </div>
            <div className="filter-item">
                <FilterLabel>Kurye Vardiya Seçim</FilterLabel>
                <PoolCourierShiftSelectionCountFilter
                    value={courierShiftSelectionCount}
                    onChange={setCourierShiftSelectionCount}
                    showAllOption={true}
                    showLabel={false}
                    selectMarginBottom="0px"
                />
            </div>
        </React.Fragment>
    );
};

export default Filters;
